import type { AccountApiResponse } from '@/employee-portal/accounts/Account.types';
import { notifyError } from '@/bugsnag';
import { useFetch } from '@vueuse/core';
import { defineStore } from 'pinia';
import { ref } from 'vue';

interface ShadowAccountStatus {
    isShadowing: boolean;
    redirect?: string;
    shadowAccountName?: string;
    shadowAccountId?: number;
}

export const useShadowAccountStore = defineStore('shadowAccount', () => {
    const isShadowing = ref(false);
    const shadowAccountName = ref('');
    const shadowAccountId = ref<number>();

    const refreshShadowStatus = async () => {
        const { data: shadowStatus, error } = await useFetch('/api/accounts/getShadowAccountStatus', {
            initialData: { isShadowing: false },
        }).json<ShadowAccountStatus>();

        if (error.value) {
            notifyError(error.value, 'shadow-account.store:refreshShadowStatus', {
                isShadowing: isShadowing.value,
                shadowAccountName: shadowAccountName.value,
            });
        } else {
            isShadowing.value = !!shadowStatus.value?.isShadowing;
            shadowAccountName.value = shadowStatus.value?.shadowAccountName ?? '';
            shadowAccountId.value = shadowStatus.value?.shadowAccountId ?? undefined;
        }
    };

    const startShadowing = async (accountId: AccountApiResponse['account']['id']) => {
        const { data: shadowStatus, error } = await useFetch(
            `/api/accounts/assignCurrentUser?account_id=${accountId}`,
        ).json<ShadowAccountStatus>();

        if (error.value) {
            notifyError(error.value, 'shadow-account.store:startShadowing', {
                isShadowing: isShadowing.value,
                shadowAccountName: shadowAccountName.value,
            });
        } else {
            isShadowing.value = !!shadowStatus.value?.isShadowing;
            shadowAccountName.value = shadowStatus.value?.shadowAccountName ?? '';
            shadowAccountId.value = shadowStatus.value?.shadowAccountId ?? undefined;
        }
    };

    const stopShadowing = async () => {
        const { data: shadowStatus, error } = await useFetch('/api/accounts/assignDefaultAccount').json<ShadowAccountStatus>();

        if (error.value) {
            notifyError(error.value, 'shadow-account.store:stopShadowing', {
                isShadowing: isShadowing.value,
                shadowAccountName: shadowAccountName.value,
            });
        } else {
            isShadowing.value = !!shadowStatus.value?.isShadowing;
            shadowAccountName.value = '';
            shadowAccountId.value = undefined;
            if (shadowStatus.value?.redirect) {
                window.location.href = shadowStatus.value.redirect;
            }
        }
    };

    refreshShadowStatus();

    return {
        isShadowing,
        shadowAccountName,
        shadowAccountId,
        refreshShadowStatus,
        startShadowing,
        stopShadowing,
    };
});
